import {
  type PortfolioBreakdownHierarchyNode,
  type PortfolioBreakdownRowData,
  type PortfolioBreakdownSubStrategy,
  type PortfolioRowData,
  type VennSeriesEntry,
} from 'venn-components';
import { getColorOnIndex, type Theme, type VennColors } from 'venn-ui-kit';
import { type CustomizableMetric, getRandomId, Numbers } from 'venn-utils';

import { selectLevelsFromHierarchy } from './selectLevelsFromHierarchy';
import { getStrategiesByLevel } from './getStrategiesByLevel';
import { mergeInvestmentsByLabel } from './mergeInvestmentsByLabel';
import { recalculateWeights } from './recalculateWeights';
import { type ColorMap, fillColorMap, shadingStrategy } from './fillColorMap';
import { findRcmStrategyColor } from './findRcmStrategyColor';
import { fillRcmColorMap } from './fillRcmColorMap';

/**
 * Special selector used with getStrategiesByLevel to refer to funds layer.
 * This is distinct from numeric strategy levels (0, 1, 2, ...).
 */
const FUNDS_HIERARCHY_LEVEL = 'funds';
const INVESTMENTS_LEVEL = 'investments';
const TOP_LEVEL_STRATEGY = 0;
const METRIC_ALLOCATION = 'allocation';

/**
 * Determines the appropriate color for a portfolio row based on RCM strategy colors or theme colors.
 * Attempts to find a predefined color based on the strategy path.
 *
 * @param row - Portfolio row data containing label, path, and hierarchy information
 * @param rowId - Current row ID
 * @param hierarchy - Complete hierarchy tree for the portfolio
 * @param colorMap - Mutable map storing ID-to-color associations
 */
export function processRcmStrategyColors(
  row: PortfolioRowData<PortfolioBreakdownRowData>,
  rowId: string,
  hierarchy: PortfolioBreakdownHierarchyNode[],
  colorMap: ColorMap,
) {
  const path = row.path.length ? row.path : [row.label];
  fillRcmColorMap(colorMap, hierarchy, path, undefined, shadingStrategy);

  const strategyColor = findRcmStrategyColor(path);
  if (strategyColor) {
    colorMap.set(rowId, strategyColor);
  }
}

/**
 * Builds a filtered and colored hierarchy tree from portfolio rows based on selected levels.
 *
 * This function processes portfolio data to create a hierarchical structure suitable for visualization.
 * It handles:
 * - Filtering hierarchy levels based on user selection
 * - Assigning colors to nodes (RCM-based or theme-based)
 * - Merging duplicate entries when requested
 * - Recalculating weights after transformations
 * - Handling both strategy and fund/investment entries
 *
 * The hierarchy structure varies based on whether top-level strategy is included:
 * - With top-level: Each row becomes a parent node with filtered children
 * - Without top-level: Filtered children are flattened to the root level
 *
 * @param rows - Array of portfolio rows containing strategy and investment data
 * @param themeColors - Theme color palette for fallback coloring
 * @param levels - Selected hierarchy levels to include (numeric for strategies, 'investments' for funds)
 * @param hasAnyStrategyInPortfolio - Whether any row represents a strategy (vs. only investments)
 * @param shouldUseRCMStrategyColors - Whether to apply RCM-based color mapping
 * @param mergeSameLabelItems - Whether to merge nodes with identical labels
 *
 * @returns Object containing the filtered hierarchy tree and color mapping
 * @returns hierarchyTree - Nested structure of portfolio breakdown nodes
 * @returns colorMap - Map of node IDs to hex color strings
 */
export function buildFilteredPortfolioHierarchy(
  rows: PortfolioRowData<PortfolioBreakdownRowData>[],
  themeColors: VennColors,
  levels: (string | number)[],
  hasAnyStrategyInPortfolio: boolean,
  shouldUseRCMStrategyColors: boolean,
  mergeSameLabelItems: boolean,
): {
  hierarchyTree: PortfolioBreakdownHierarchyNode[];
  colorMap: ColorMap;
} {
  const colorMap = new Map() as ColorMap;
  // Empty sections color
  colorMap.set('', '#ffffff');

  if (!rows?.length) {
    return {
      hierarchyTree: [],
      colorMap,
    };
  }

  const strategyLevels = levels.filter((val) => typeof val === 'number').sort();
  // Strategy levels excluding TOP_LEVEL_STRATEGY, shifted by -1 for slicing
  const innerStrategyLevels = strategyLevels.filter((level) => level !== TOP_LEVEL_STRATEGY).map((level) => level - 1);
  const hasTopLevelStrategy = strategyLevels.includes(TOP_LEVEL_STRATEGY);
  const shouldKeepFunds = levels.includes(INVESTMENTS_LEVEL) || !hasAnyStrategyInPortfolio || !strategyLevels.length;
  const shouldKeepTopLevelFunds = shouldKeepFunds && levels.length === 1 && levels[0] === INVESTMENTS_LEVEL;
  let hierarchyTree: PortfolioBreakdownHierarchyNode[] = [];

  rows.forEach((row) => {
    const { hierarchy = [], weight } = row.value[0] || {};

    if (!weight) {
      return;
    }

    const rowId = getRandomId();

    if (!row.isStrategy) {
      if (shouldKeepTopLevelFunds) {
        colorMap.set(rowId, getColorOnIndex(themeColors, hierarchyTree.length));
        hierarchyTree.push({
          id: rowId,
          label: row.label,
          allocation: Number(row.allocation[0]) || 0,
          weight,
          isFund: true,
          fundId: row.fundId,
        });
      }

      return;
    }

    if (shouldUseRCMStrategyColors) {
      processRcmStrategyColors(row, rowId, hierarchy, colorMap);
    }

    const filteredHierarchy = selectLevelsFromHierarchy(hierarchy, innerStrategyLevels, {
      keepFunds: shouldKeepFunds,
      mergeSameLabelItems,
    });

    const itemsToPush = hasTopLevelStrategy
      ? [
          {
            id: rowId,
            label: row.label,
            children: filteredHierarchy,
            allocation: Number(row.allocation[0]) || 0,
            weight,
          },
        ]
      : filteredHierarchy;

    itemsToPush.forEach((hierarchyItem, i) => {
      const rowColor = colorMap.get(hierarchyItem.id) || getColorOnIndex(themeColors, hierarchyTree.length + i);
      colorMap.set(hierarchyItem.id, rowColor);

      if (hierarchyItem.children) {
        fillColorMap(hierarchyItem.children, shadingStrategy, rowColor, colorMap);
      }
    });

    hierarchyTree.push(...itemsToPush);
  });

  if (mergeSameLabelItems) {
    // Manually merging because top-level data doesn't yet handle duplicate entries
    hierarchyTree = mergeInvestmentsByLabel(hierarchyTree);
  }

  hierarchyTree = recalculateWeights(hierarchyTree);

  return { hierarchyTree, colorMap };
}

/**
 * Creates a chart series entry from strategy data for visualization.
 *
 * Transforms strategy breakdown data into a format suitable for pie charts.
 * Each strategy becomes a data point with:
 * - Display name and legend text
 * - Weight/percentage values
 * - Color mapping
 * - Path information for drill-down navigation
 *
 * @param strategies - Array of strategy breakdown data with labels, weights, and paths
 * @param colorMap - Map of strategy IDs to hex color strings
 * @param metricLabel - Display label for the metric (e.g., 'Allocation', 'Value')
 * @param isMetricAllocation - Whether the metric represents allocation (affects weight calculation)
 *
 * @returns Chart series entry with formatted data points
 */
export function createChartSeriesEntry(
  strategies: PortfolioBreakdownSubStrategy[],
  colorMap: ColorMap,
  metricLabel: string | undefined,
  isMetricAllocation: boolean,
): VennSeriesEntry {
  return {
    name: metricLabel,
    data: strategies.map((strategy) => {
      const weight = isMetricAllocation ? (strategy.weight.at(-1) ?? 0) : 0;

      return {
        name: strategy.label,
        legend: strategy.id ? `${Numbers.safeFormatPercentage(weight, 1)} ${strategy.label}` : undefined,
        aggregatedPercentage: weight,
        y: weight,
        color: colorMap.get(strategy.id),
        path: [strategy.label, ...strategy.path],
        fundId: strategy.fundId,
        parentLabel: strategy.parentLabel,
      };
    }),
  };
}

/**
 * Builds complete chart data for portfolio breakdown visualization.
 *
 * This is the main entry point for generating portfolio breakdown chart data. It orchestrates:
 * 1. Hierarchy tree construction with filtered levels
 * 2. Color mapping (RCM-based or theme-based)
 * 3. Series entry generation for each selected level
 * 4. Fund/investment series inclusion when applicable
 *
 * The function handles multiple hierarchy levels and creates separate series entries for each level,
 * enabling multi-level drill-down visualization in pie/donut charts.
 *
 * **Level Selection:**
 * - Numeric values (0, 1, 2, ...) represent strategy hierarchy depths (0 = top-level)
 * - 'investments' string includes individual funds/investments as a separate series
 *
 * **Allocation Metric:**
 * - When metric.key === 'allocation', weights are calculated by multiplying across hierarchy levels
 * - For other metrics, weights default to 0
 *
 * @param rows - Portfolio rows containing strategy and investment data
 * @param theme - Theme object providing color palettes
 * @param metric - Metric configuration (key and label) for display and calculation
 * @param levels - Selected hierarchy levels (numeric for strategies, 'investments' for funds)
 * @param shouldUseRCMStrategyColors - Optional. Enables RCM-based color mapping keyed by strategy path
 * @param mergeSameLabelItems - Optional. Merges hierarchy nodes with identical labels
 *
 * @returns Object containing color map, hierarchy tree, and chart series entries
 * @returns colorMap - Map of node IDs to hex color strings
 * @returns hierarchyTree - Complete filtered hierarchy structure
 * @returns seriesEntries - Array of chart series (one per selected level, plus optional funds series)
 */
export function getPortfolioBreakdownChartData(
  rows: PortfolioRowData<PortfolioBreakdownRowData>[],
  theme: Theme,
  metric: Pick<CustomizableMetric, 'key' | 'label'> | undefined,
  levels: (string | number)[],
  shouldUseRCMStrategyColors?: boolean,
  mergeSameLabelItems?: boolean,
): {
  colorMap: ColorMap;
  hierarchyTree: PortfolioBreakdownHierarchyNode[];
  seriesEntries: VennSeriesEntry[];
} {
  if (!rows?.length) {
    return { colorMap: new Map(), hierarchyTree: [], seriesEntries: [] };
  }

  const isMetricAllocation = metric?.key === METRIC_ALLOCATION;
  const hasAnyStrategyInPortfolio = rows.some((row) => row.isStrategy);

  const strategyLevels = levels.filter((val) => typeof val === 'number').sort();
  const shouldKeepFunds = levels.includes(INVESTMENTS_LEVEL) || !hasAnyStrategyInPortfolio || !strategyLevels.length;

  const { hierarchyTree, colorMap } = buildFilteredPortfolioHierarchy(
    rows,
    theme.Colors,
    levels,
    hasAnyStrategyInPortfolio,
    shouldUseRCMStrategyColors ?? false,
    mergeSameLabelItems ?? false,
  );

  const topLevelAllocation = hierarchyTree.map(({ allocation }) => allocation).reduce((a, b) => a + b, 0);

  const seriesEntries = strategyLevels.map((_, levelIndex) => {
    const strategiesByLevel = getStrategiesByLevel(hierarchyTree, levelIndex, topLevelAllocation);
    return createChartSeriesEntry(strategiesByLevel, colorMap, metric?.label, isMetricAllocation);
  });

  if (shouldKeepFunds) {
    const funds = getStrategiesByLevel(hierarchyTree, FUNDS_HIERARCHY_LEVEL, topLevelAllocation);
    seriesEntries.push(createChartSeriesEntry(funds, colorMap, metric?.label, isMetricAllocation));
  }

  return {
    colorMap,
    hierarchyTree,
    seriesEntries,
  };
}
